import {
  useActiveSupplier,
  useSupplier,
  useUpdateSupplier,
} from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { Supplier } from '@/core/domain/people/Suppliers/Suppliers.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';
import { Form } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';

type Props = PropsWithChildren<unknown>;
type Forms = {
  supplierGeneral?: Supplier;
};

type SupplierFormContextProps = {
  isLoading: boolean;
  forms: Forms;
  supplierId: string;
  disabled: boolean;
  activeSupplier: boolean;
  setActiveSupplier: (isActive: boolean) => void;
  handleSubmit: () => void;
  handleActive: () => void;
  loadActive: boolean;
  setLoadActive: (isActive: boolean) => void;
  supplier?: Supplier | null;
  loadingDetail: boolean;
};

const SupplierFormContext = createContext({} as SupplierFormContextProps);
export const useSupplierFormContext = () => useContext(SupplierFormContext);

const SupplierFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { supplierId } = useRouteMatch<{ supplierId: string }>().params;

  const supplierData = useSupplier(Number(supplierId));

  // State
  const [forms, setForms] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeSupplier, setActiveSupplier] = useState<boolean>(true);
  const [loadActive, setLoadActive] = useState<boolean>(true);

  // Request hook
  const supplierGeneralUpdate = useUpdateSupplier();
  const activeSupplierHook = useActiveSupplier();

  const disabled =
    !forms.supplierGeneral || !Object.keys(forms.supplierGeneral).length;

  useServiceError(supplierGeneralUpdate.error);

  useEffect(() => {
    setForms({});
  }, [supplierId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setForms((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleActive() {
    setLoadActive(true);
    if (supplierId) {
      try {
        await activeSupplierHook
          .mutateAsync({ id: Number(supplierId), activeSupplier })
          .then(() => setActiveSupplier(!activeSupplier))
          .then(() => setLoadActive(false));
      } catch {}
    }
  }

  async function handleSubmit() {
    setIsLoading(true);
    try {
      if (
        forms.supplierGeneral &&
        Object.keys(forms.supplierGeneral).some(Boolean) &&
        typeof supplierId === 'string'
      ) {
        await supplierGeneralUpdate.mutateAsync({
          ...forms.supplierGeneral,
          active: activeSupplier,
          id: +supplierId,
          cpf: removeCpfCnpjMask(forms.supplierGeneral.cpf),
          cnpj: removeCpfCnpjMask(forms.supplierGeneral.cnpj),
        });
      }
      setForms({});
    } catch (error: unknown) {
      const err = error as AxiosError;

      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }

    setIsLoading(false);
  }

  return (
    <SupplierFormContext.Provider
      value={{
        isLoading,
        forms,
        supplierId,
        disabled,
        handleActive,
        handleSubmit,
        setActiveSupplier,
        activeSupplier,
        setLoadActive,
        loadActive,
        supplier: supplierData.data,
        loadingDetail: supplierData.isLoading,
      }}
    >
      <Form.Provider onFormChange={handleFormChange}>{children}</Form.Provider>
    </SupplierFormContext.Provider>
  );
};

export default SupplierFormProvider;
